'use client';
import { CustomerFieldsEnum } from '@/constants/customerService';
import { getI18nLang } from '@/i18n/settings';
import { JDILogin } from '@/lib/jdiClientUtils';
import { formatLocale } from '@/utils/zendesk';
import Script from 'next/script';
import { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';

interface Props {
  ref: any;
  serviceFields?: FieldsParams[];
}

interface FieldsParams {
  id: string;
  value: string;
}

// 重试机制辅助函数
const withRetry = async (fn: () => Promise<any>, maxRetries = 5, delay = 500) => {
  let retries = 0;

  const attempt = async () => {
    try {
      return await fn();
    } catch (error) {
      retries++;
      if (retries <= maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return attempt();
      }
      throw error;
    }
  };

  return attempt();
};

const CustomerService: React.FC<Props> = forwardRef(({ serviceFields = [] }, ref) => {
  const zELang = formatLocale(getI18nLang());
  const serviceFieldsRef = useRef(serviceFields);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [zeReady, setZeReady] = useState(false);
  const [checkCompleted, setCheckCompleted] = useState(false);

  // 保存是否需要打开客服的状态
  const shouldOpenService = useRef(false);

  useEffect(() => {
    serviceFieldsRef.current = serviceFields;
  }, [serviceFields]);

  // 页面加载时检查是否需要打开客服
  useEffect(() => {
    const checkOnPageLoad = () => {
      const isOpenCustomer = sessionStorage.getItem('OpenCustomerFromLogin');
      if (isOpenCustomer) {
        shouldOpenService.current = true;
        // 立即检查一次
        checkShouldOpenService();
      }
    };

    // 组件挂载时检查
    checkOnPageLoad();

    // 监听页面加载完成
    const handleLoad = () => {
      checkOnPageLoad();
    };

    window.addEventListener('load', handleLoad);
    return () => window.removeEventListener('load', handleLoad);
  }, []);

  // 监听SDK加载状态和zE初始化状态
  useEffect(() => {
    if (sdkLoaded && !zeReady) {
      // 轮询检查zE是否可用
      const checkInterval = setInterval(() => {
        if (window.zE && typeof window.zE === 'function') {
          setZeReady(true);
          clearInterval(checkInterval);
        }
      }, 200);

      // 超时保护
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
      }, 10000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }
  }, [sdkLoaded, zeReady]);

  // 当zE准备好时检查是否需要打开
  useEffect(() => {
    if (zeReady && !checkCompleted) {
      checkShouldOpenService();
    }
  }, [zeReady]);

  useImperativeHandle(
    ref,
    () => ({
      openService: () => withRetry(openService),
    }),
    [],
  );

  // 检查是否需要在登录后打开客服
  const checkShouldOpenService = () => {
    if (shouldOpenService.current && zeReady) {
      withRetry(openService)
        .then(() => {
          setCheckCompleted(true);
        })
        .catch((err) => {
          setCheckCompleted(true);
        });
    }
  };

  const openService = async () => {
    try {
      if (!window.zE) {
        throw new Error('window.zE 未定义');
      }
      window.zE('messenger', 'open');
      await getLogin();
      return true;
    } catch (err) {
      console.log(err);
    }
  };

  // 获取用户登录信息
  const getLogin = async () => {
    try {
      const { isLoggedIn, userInfo } = await JDILogin.getInstance().getUserInfo();

      const fieldsParams: FieldsParams[] = [];
      if (isLoggedIn) {
        fieldsParams.push(
          { id: CustomerFieldsEnum.PIN, value: userInfo.pin },
          { id: CustomerFieldsEnum.SITE, value: userInfo.siteCode },
        );

        if (window.zE) {
          window.zE('messenger:set', 'conversationFields', [
            ...fieldsParams,
            ...serviceFieldsRef.current,
          ]);
        }
        // 打开客服后清空 sessionStorage
        sessionStorage.removeItem('OpenCustomerFromLogin');
        shouldOpenService.current = false;
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Script
      strategy='afterInteractive'
      crossOrigin='anonymous'
      async
      nonce='{random}'
      id='ze-snippet'
      src='https://static.zdassets.com/ekr/snippet.js?key=e6e37e45-40ea-4b86-895b-2da81050b849'
      onLoad={() => {
        // 立即尝试初始化
        if (window.zE) {
          window.zE('messenger', 'close');
          window.zE('messenger:set', 'locale', zELang);
        }
      }}
      onReady={() => {
        if (window?.zE) {
          setSdkLoaded(true);
        }
      }}
      onError={(e) => {
        setSdkLoaded(false);
        console.error('客服SDK加载失败', e);
      }}
    />
  );
});

CustomerService.displayName = 'CustomerService';
export default CustomerService;
